class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't always get triggered. A timeout helps
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      // FIX (ASSC): target elements by id first. Previously the cart icon entry used
      // document.querySelector('.shopify-section'), which replaced the FIRST section on
      // the page (announcement bar / site styles) with the cart icon after add to cart.
      let sectionElement;
      if (section.target) {
        sectionElement = document.querySelector(section.target);
      } else {
        sectionElement = document.getElementById(section.id);
      }

      if (!sectionElement) return;
      const html = parsedState.sections && parsedState.sections[section.id];
      if (!html) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner == null) return;
      sectionElement.innerHTML = inner;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.querySelector('cart-drawer-items')?.offsetHeight;

      // ASSC: don't pop the drawer open — show an "Added to cart" toast by the cart icon
      this.showAddedToast(parsedState);

      // Remove upsell items that are now in cart
      const upsellItems = document.querySelectorAll('[data-upsell-variant-id]');
      const cartItems = JSON.parse(localStorage.getItem('cart'))?.items || [];

      upsellItems.forEach((item) => {
        const upsellVariantId = parseInt(item.dataset.upsellVariantId, 10);
        const isInCart = cartItems.some((cartItem) => cartItem.variant_id === upsellVariantId);

        if (isInCart) {
          item.remove();
        }
      });
    });
  }

  /* ---------- ASSC "Added to cart" toast ---------- */

  ensureToastStyles() {
    if (document.getElementById('assc-cart-toast-styles')) return;
    const style = document.createElement('style');
    style.id = 'assc-cart-toast-styles';
    style.textContent = `
      .assc-cart-toast {
        position: fixed;
        z-index: 1001;
        top: 0;
        right: 1.6rem;
        width: min(34rem, calc(100vw - 3.2rem));
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 1.4rem;
        align-items: center;
        padding: 1.2rem 1.4rem;
        border-radius: 1.8rem;
        background: rgba(var(--color-background), 0.92);
        -webkit-backdrop-filter: saturate(160%) blur(16px);
        backdrop-filter: saturate(160%) blur(16px);
        border: 1px solid rgba(var(--color-foreground), 0.18);
        box-shadow: 0 22px 48px -22px rgba(var(--color-foreground), 0.55);
        color: rgb(var(--color-foreground));
        opacity: 0;
        transform: translateY(-10px) scale(0.96);
        transform-origin: top right;
        transition: opacity 0.25s ease, transform 0.35s cubic-bezier(0.34, 1.36, 0.64, 1);
        pointer-events: none;
      }
      .assc-cart-toast.is-visible {
        opacity: 1;
        transform: none;
        pointer-events: auto;
      }
      .assc-cart-toast::before {
        content: '';
        position: absolute;
        top: -0.7rem;
        right: var(--assc-toast-arrow, 2rem);
        width: 1.4rem;
        height: 1.4rem;
        background: inherit;
        border-left: 1px solid rgba(var(--color-foreground), 0.18);
        border-top: 1px solid rgba(var(--color-foreground), 0.18);
        transform: rotate(45deg);
        border-top-left-radius: 0.3rem;
      }
      /* Photo tile: the photo itself is rounded/clipped so the check badge can sit on the corner without being cut off */
      .assc-cart-toast__media {
        position: relative;
        width: 5.6rem;
        height: 5.6rem;
        overflow: visible;
        flex: 0 0 auto;
      }
      .assc-cart-toast__media img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
        border-radius: 1.2rem;
        background: rgba(var(--color-foreground), 0.06);
      }
      .assc-cart-toast__check {
        position: absolute;
        right: -0.6rem;
        bottom: -0.6rem;
        z-index: 1;
        width: 2.2rem;
        height: 2.2rem;
        border-radius: 50%;
        background: rgb(var(--color-foreground));
        color: rgb(var(--color-background));
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 0 2.5px rgb(var(--color-background)), 0 4px 10px -4px rgba(var(--color-foreground), 0.6);
        transform: scale(0);
        transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s;
      }
      .assc-cart-toast.is-visible .assc-cart-toast__check { transform: scale(1); }
      .assc-cart-toast__check svg { width: 1.2rem; height: 1.2rem; display: block; }
      .assc-cart-toast__title {
        display: block;
        font-size: 1.15rem;
        font-weight: 700;
        letter-spacing: 0.16rem;
        text-transform: uppercase;
        margin-bottom: 0.3rem;
      }
      .assc-cart-toast__name {
        display: block;
        font-size: 1.3rem;
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .assc-cart-toast__variant {
        display: block;
        font-size: 1.15rem;
        opacity: 0.7;
        margin-top: 0.2rem;
      }
      .assc-cart-toast__actions {
        grid-column: 1 / -1;
        display: flex;
        gap: 0.8rem;
        margin-top: 0.2rem;
      }
      .assc-cart-toast__btn {
        flex: 1;
        min-height: 3.8rem;
        border-radius: 999px;
        border: 1px solid rgba(var(--color-foreground), 0.3);
        background: transparent;
        color: rgb(var(--color-foreground));
        font: inherit;
        font-size: 1.1rem;
        font-weight: 700;
        letter-spacing: 0.12rem;
        text-transform: uppercase;
        cursor: pointer;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.2s ease;
      }
      .assc-cart-toast__btn:hover { background: rgba(var(--color-foreground), 0.08); }
      .assc-cart-toast__btn--solid {
        background: rgb(var(--color-foreground));
        color: rgb(var(--color-background));
        border-color: rgb(var(--color-foreground));
      }
      .assc-cart-toast__btn--solid:hover { background: rgb(var(--color-foreground)); opacity: 0.9; }
      .assc-cart-toast__bar {
        position: absolute;
        left: 1.8rem;
        right: 1.8rem;
        bottom: 0;
        height: 2px;
        border-radius: 2px;
        background: rgb(var(--color-foreground));
        transform-origin: left center;
        opacity: 0.5;
      }
      .assc-cart-toast.is-visible .assc-cart-toast__bar {
        animation: asscToastBar var(--assc-toast-ms, 4000ms) linear forwards;
      }
      .assc-cart-toast:hover .assc-cart-toast__bar { animation-play-state: paused; }
      @keyframes asscToastBar { from { transform: scaleX(1); } to { transform: scaleX(0); } }

      #cart-icon-bubble.assc-cart-bump { animation: asscCartBump 0.6s cubic-bezier(0.34, 1.56, 0.64, 1); }
      #cart-icon-bubble.assc-cart-bump .cart-count-bubble { animation: asscCountPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1); }
      @keyframes asscCartBump {
        0% { transform: scale(1); }
        30% { transform: scale(1.22) rotate(-8deg); }
        55% { transform: scale(0.94) rotate(4deg); }
        100% { transform: scale(1) rotate(0); }
      }
      @keyframes asscCountPop {
        0% { transform: scale(0.4); }
        60% { transform: scale(1.35); }
        100% { transform: scale(1); }
      }
      @media (prefers-reduced-motion: reduce) {
        .assc-cart-toast, .assc-cart-toast__check { transition: opacity 0.2s ease; transform: none !important; }
        #cart-icon-bubble.assc-cart-bump, #cart-icon-bubble.assc-cart-bump .cart-count-bubble { animation: none; }
      }
    `;
    document.head.appendChild(style);
  }

  showAddedToast(parsedState) {
    this.ensureToastStyles();
    const DURATION = 4000;

    if (this.toastTimer) clearTimeout(this.toastTimer);
    if (this.toastEl) this.toastEl.remove();

    const esc = (s) =>
      String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    const title = parsedState.product_title || parsedState.title || 'Item';
    let variant = parsedState.variant_title && parsedState.variant_title !== 'Default Title' ? parsedState.variant_title : '';
    if (variant) variant = variant.replace(/\s*\/\s*/g, ' · ');
    let img = parsedState.featured_image && parsedState.featured_image.url ? parsedState.featured_image.url : parsedState.image || '';
    if (img && img.indexOf('?') === -1) img += '?width=160';
    else if (img) img += '&width=160';

    const toast = document.createElement('div');
    toast.className = 'assc-cart-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.style.setProperty('--assc-toast-ms', DURATION + 'ms');
    toast.innerHTML = `
      <div class="assc-cart-toast__media">
        ${img ? `<img src="${esc(img)}" alt="">` : ''}
        <span class="assc-cart-toast__check" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5L19 7"/></svg>
        </span>
      </div>
      <div style="min-width:0">
        <span class="assc-cart-toast__title">Added to cart</span>
        <span class="assc-cart-toast__name">${esc(title)}</span>
        ${variant ? `<span class="assc-cart-toast__variant">${esc(variant)}</span>` : ''}
      </div>
      <div class="assc-cart-toast__actions">
        <button type="button" class="assc-cart-toast__btn" data-assc-toast="view">View cart</button>
        <a href="/checkout" class="assc-cart-toast__btn assc-cart-toast__btn--solid" data-assc-toast="checkout">Checkout</a>
      </div>
      <span class="assc-cart-toast__bar" aria-hidden="true"></span>
    `;
    document.body.appendChild(toast);
    this.toastEl = toast;

    // Position under the cart icon, arrow pointing at it
    const icon = document.getElementById('cart-icon-bubble');
    const place = () => {
      if (!icon) {
        toast.style.top = '1.6rem';
        return;
      }
      const r = icon.getBoundingClientRect();
      const top = Math.max(r.bottom + 12, 12);
      const right = Math.max(window.innerWidth - r.right - 4, 12);
      toast.style.top = top + 'px';
      toast.style.right = right + 'px';
      const tr = toast.getBoundingClientRect();
      const arrowRight = Math.min(Math.max(tr.right - (r.left + r.width / 2) - 7, 14), tr.width - 28);
      toast.style.setProperty('--assc-toast-arrow', arrowRight + 'px');
    };
    place();

    // Bump the cart icon + count
    if (icon) {
      icon.classList.remove('assc-cart-bump');
      void icon.offsetWidth;
      icon.classList.add('assc-cart-bump');
      setTimeout(() => icon.classList.remove('assc-cart-bump'), 700);
    }

    requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('is-visible')));

    const hide = () => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 400);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', place);
    };
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', place);

    let remaining = DURATION;
    let started = Date.now();
    const startTimer = () => {
      started = Date.now();
      this.toastTimer = setTimeout(hide, remaining);
    };
    toast.addEventListener('mouseenter', () => {
      clearTimeout(this.toastTimer);
      remaining -= Date.now() - started;
    });
    toast.addEventListener('mouseleave', () => startTimer());
    startTimer();

    toast.querySelector('[data-assc-toast="view"]').addEventListener('click', () => {
      clearTimeout(this.toastTimer);
      hide();
      this.open(icon || undefined);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
        target: 'cart-drawer .drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
  

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
